import { Controller } from "@hotwired/stimulus"

// Clean, standardized filter controller for the new Admin::FilterComponent
export default class extends Controller {
  static targets = ["toggleButton", "toggleIcon", "filterPanel"]
  static values = { debounce: { type: Number, default: 500 } }

  connect() {
    this.debounceTimer = null
    this.isSubmitting = false
    
    // Set up keyboard shortcuts
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.element.removeEventListener('keydown', this.handleKeydown.bind(this))
  }

  // Toggle the collapsible filter panel
  toggleFilters() {
    if (!this.hasFilterPanelTarget || !this.hasToggleButtonTarget || !this.hasToggleIconTarget) {
      return
    }

    const isExpanded = this.toggleButtonTarget.getAttribute('aria-expanded') === 'true'
    
    if (isExpanded) {
      this.filterPanelTarget.classList.add('hidden')
      this.toggleButtonTarget.setAttribute('aria-expanded', 'false')
      this.toggleIconTarget.style.transform = 'rotate(0deg)'
    } else {
      this.filterPanelTarget.classList.remove('hidden')
      this.toggleButtonTarget.setAttribute('aria-expanded', 'true')
      this.toggleIconTarget.style.transform = 'rotate(180deg)'
    }
  }

  // Debounced form submission for search input
  debounceSubmit(event) {
    if (this.isSubmitting) return

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.submitForm(event.target.closest('form'))
    }, this.debounceValue)
  }

  // Immediate form submission for filter changes
  submit(event) {
    if (this.isSubmitting) return
    
    // Small delay to prevent rapid-fire submissions
    setTimeout(() => {
      this.submitForm(event.target.closest('form'))
    }, 50)
  }

  // Clear the search input and submit
  clearSearch(event) {
    event.preventDefault()
    if (this.isSubmitting) return

    const searchInput = this.element.querySelector('input[name="search"]')
    if (searchInput) {
      searchInput.value = ''
      this.submitForm(searchInput.closest('form'))
    }
  }

  // Remove a specific filter and submit
  removeFilter(event) {
    event.preventDefault()
    if (this.isSubmitting) return

    const fieldName = event.target.closest('[data-filter-field]').dataset.filterField
    this.removeUrlParam(fieldName)
  }

  // Remove a specific value from a multi-select filter
  removeFilterValue(event) {
    event.preventDefault()
    if (this.isSubmitting) return

    const button = event.target.closest('[data-filter-field]')
    if (!button) {
      console.error('No button with data-filter-field found for removeFilterValue')
      return
    }
    
    const fieldName = button.dataset.filterField
    const valueToRemove = button.dataset.filterValue

    const url = new URL(window.location)
    
    // Get all current values for this field (handles both single and array formats)
    let allCurrentValues = []
    let usingArrayFormat = false
    
    // Check simple format first (role=admin,user)
    const simpleValue = url.searchParams.get(fieldName)
    if (simpleValue) {
      allCurrentValues = simpleValue.split(',').map(v => v.trim())
    } else {
      // Check array format (role[]=admin&role[]=user) 
      const arrayValues = url.searchParams.getAll(`${fieldName}[]`)
      if (arrayValues.length > 0) {
        allCurrentValues = arrayValues
        usingArrayFormat = true
      }
    }

    if (allCurrentValues.length > 0) {
      // Remove the specific value
      const remainingValues = allCurrentValues.filter(v => v !== valueToRemove)
      
      // Clear all existing parameters for this field
      url.searchParams.delete(fieldName)
      url.searchParams.delete(`${fieldName}[]`)
      
      // Add back the remaining values in the same format
      if (remainingValues.length > 0) {
        if (usingArrayFormat) {
          // Re-add as array format
          remainingValues.forEach(value => {
            url.searchParams.append(`${fieldName}[]`, value)
          })
        } else {
          // Re-add as simple comma-separated format
          url.searchParams.set(fieldName, remainingValues.join(','))
        }
      }
      
      url.searchParams.delete('page') // Reset pagination
      window.location.href = url.toString()
    }
  }

  // Clear date range filters
  clearDateRange(event) {
    event.preventDefault()
    if (this.isSubmitting) return

    const fieldName = event.target.closest('[data-filter-field]').dataset.filterField
    const url = new URL(window.location)
    url.searchParams.delete(`${fieldName}_from`)
    url.searchParams.delete(`${fieldName}_to`)
    url.searchParams.delete('page') // Reset pagination
    window.location.href = url.toString()
  }

  // Handle keyboard shortcuts
  handleKeydown(event) {
    // Escape key clears all filters
    if (event.key === 'Escape') {
      event.preventDefault()
      this.clearAllFilters()
      return
    }

    // Ctrl+Enter or Cmd+Enter submits form immediately
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault()
      const form = event.target.closest('form')
      if (form) this.submitForm(form)
      return
    }

    // Alt+F toggles filters
    if (event.altKey && event.key === 'f') {
      event.preventDefault()
      this.toggleFilters()
      return
    }
  }

  // Private methods
  submitForm(form) {
    if (!form || this.isSubmitting) return

    this.isSubmitting = true
    
    try {
      // Add visual feedback
      this.showLoadingState()
      
      // Build clean URL by collecting only non-empty form values
      const formData = new FormData(form)
      const url = new URL(form.action || window.location.href)
      
      // Clear existing search params
      url.search = ''
      
      // Add only non-empty parameters (keeping Rails array conventions)
      for (const [key, value] of formData.entries()) {
        if (value && value.toString().trim() !== '') {
          url.searchParams.append(key, value)
        }
      }
      
      // Navigate to clean URL
      window.location.href = url.toString()
    } catch (error) {
      console.error('Filter submission error:', error)
      this.hideLoadingState()
      this.showError('Failed to apply filters. Please try again.')
    }
  }

  removeUrlParam(paramName) {
    const url = new URL(window.location)
    url.searchParams.delete(paramName)
    url.searchParams.delete('page') // Reset pagination
    window.location.href = url.toString()
  }

  clearAllFilters() {
    if (this.isSubmitting) return
    
    // Navigate to clean URL (form action without parameters)
    const form = this.element.querySelector('form')
    if (form) {
      window.location.href = form.action
    }
  }

  showLoadingState() {
    // Add subtle loading visual feedback
    this.element.style.opacity = '0.7'
    this.element.style.pointerEvents = 'none'
    
    // Add loading cursor
    document.body.style.cursor = 'wait'
  }

  hideLoadingState() {
    this.element.style.opacity = ''
    this.element.style.pointerEvents = ''
    document.body.style.cursor = ''
    this.isSubmitting = false
  }

  showError(message) {
    // Create or update error notification
    let errorDiv = document.getElementById('filter-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.id = 'filter-error'
      errorDiv.className = 'fixed top-4 right-4 max-w-sm bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg z-50'
      document.body.appendChild(errorDiv)
    }

    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <div class="ml-auto pl-3">
          <button type="button" class="inline-flex text-red-400 hover:text-red-600" onclick="this.parentElement.parentElement.parentElement.remove()">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv && errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 5000)
  }
}